import { useOverlayTriggerState } from '@react-stately/overlays'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { Button } from 'blaze-ui/Button'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { CalendarTrash, ChevronDown } from 'blaze-ui/icons'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DesignActionType } from 'types'
import { useCompleteDesignAction } from 'components/Design/utils/useCompleteDesignAction'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { AlmanacFileIndexItem } from 'hooks/queries/files/types'
import { useUpdateFile } from 'hooks/queries/files/useUpdateFile'
import Styles from './PostAtCell.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { usePublishScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/usePublishScheduledPostTargets'
import { usePublishScheduledPost } from 'hooks/queries/scheduledPosts/usePublishScheduledPost'
import { Send2 } from 'blaze-ui/icons/16'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { PostTargetsPublishingSync } from './PostTargetsPublishingSync'
import { useQueryClient } from 'react-query'
import { optimisticUpdateFileInInfiniteLists } from 'hooks/queries/files/cacheUtils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ScheduledPostTarget } from 'types'
import { CROSS_POST_SUPPORTED_PROVIDERS } from 'api/clients/ExternalIntegrationsClient'
import { SOCIAL_VIDEO_PLATFORMS } from 'screens/Calendar/FullScreenPreview/utils'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { supportedBlogProviders } from 'components/ArticlePostPreview/utils'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import cx from 'classnames'

interface PostAtCellProps {
  item: AlmanacFileIndexItem
  fileChannel: ChannelType | DesignChannel | GenerationType
}

export const PostAtCell = ({ item, fileChannel }: PostAtCellProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { currentFolder } = useCurrentFolderStore()
  const completeActionMutation = useCompleteDesignAction()
  const [isLoading, setIsLoading] = useState(false)
  const overlayState = useOverlayTriggerState({})
  const [calendarPickerPostAt, setCalendarPickerPostAt] = useState<string | null>(item.post_at || null)
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const updateFileMutation = useUpdateFile(item.key)
  const publishTargetsMutation = usePublishScheduledPostTargets()
  const { data: integrationsData } = useExternalIntegrations()

  const invalidateRecentsQuery = useCallback(() => {
    queryClient.invalidateQueries(filesKeys.partialRecentsInfinite(workspaceId))
  }, [queryClient, workspaceId])

  const { mutateAsync: publishScheduledPost } = usePublishScheduledPost({
    onSuccess: () => {
      invalidateRecentsQuery()
    },
  })

  const failedTargetIds = (item.scheduled_post_targets ?? [])
    .filter((target) => target.status === 'failed')
    .map((target) => target.id)
  const hasFailedTargets = failedTargetIds.length > 0
  const hasTargets = (item.scheduled_post_targets ?? []).length > 0
  const scheduledPostId = item.scheduled_post_id ?? item.scheduled_post_targets?.[0]?.scheduled_post_id

  const scheduledPostProvider = useMemo(() => item.scheduled_post_targets?.[0]?.provider, [item.scheduled_post_targets])
  const isCrossPostSupported = scheduledPostProvider && CROSS_POST_SUPPORTED_PROVIDERS.includes(scheduledPostProvider)
  const isVideoCrossPostSupported = scheduledPostProvider && SOCIAL_VIDEO_PLATFORMS.includes(scheduledPostProvider)
  const isBlogPost = fileChannel === ChannelType.BlogPost
  const canEdit = currentFolder?.has_edit_access || !currentFolder

  const hasPublishingTargets = useMemo(
    () =>
      (item.scheduled_post_targets ?? []).some(
        (target) => target.status === 'queued' || target.status === 'processing' || target.status === 'retrying',
      ),
    [item.scheduled_post_targets],
  )

  const [shouldSyncTargets, setShouldSyncTargets] = useState(hasPublishingTargets)
  const [isPublishingTargets, setIsPublishingTargets] = useState(hasPublishingTargets)

  useEffect(() => {
    if (hasPublishingTargets) {
      setShouldSyncTargets(true)
    }
    setIsPublishingTargets(hasPublishingTargets)
  }, [hasPublishingTargets])

  const isPostNowLoading = publishTargetsMutation.isLoading || isPublishingTargets

  let canPostNow: boolean | undefined
  if (isBlogPost) {
    canPostNow = !!integrationsData?.external_integrations.find((integration) =>
      supportedBlogProviders.includes(integration.provider),
    )
  } else {
    canPostNow = hasTargets
  }

  const disablePostNowButton = !scheduledPostId || !canPostNow || isPostNowLoading

  const postNowButtonText = useMemo(() => {
    if (isPostNowLoading) {
      return 'Posting...'
    }
    if (hasFailedTargets) {
      return 'Retry Failed'
    }
    return 'Post Now'
  }, [isPostNowLoading, hasFailedTargets])

  const handleSchedule = async (date: Date, value: PostStatusPropertyValue) => {
    if (!item) return

    setIsLoading(true)

    try {
      const formattedDate = dayjs(date).format('ddd, MMM DD, h:mm A')
      const dateISOString = date.toISOString()

      if (item.post_status === PostStatusPropertyValue.AutoPostScheduled) {
        // Update existing scheduled post - just change the date
        await updateFileMutation.mutateAsync({
          post_at: dateISOString,
        })
        showToastMessage(`Post Updated For ${formattedDate}`, 'success')
      } else {
        // Create new calendar item - set post_at and status
        await updateFileMutation.mutateAsync({
          post_at: dateISOString,
          post_status: PostStatusPropertyValue.AutoPostScheduled,
        })
        showToastMessage(`Post Scheduled For ${formattedDate}`, 'success')
      }

      if (item.asset_type === 'Design') {
        completeActionMutation.mutate({ action: DesignActionType.SchedulePost })
      }
      invalidateRecentsQuery()
    } catch (error) {
      showToastMessage(`Failed to schedule post`, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleUnschedule = async (close: () => void) => {
    setIsLoading(true)

    try {
      if (item.post_status === PostStatusPropertyValue.AutoPostScheduled) {
        // Unschedule: set status to draft but keep the date
        await updateFileMutation.mutateAsync({
          post_status: PostStatusPropertyValue.Draft,
        })
        showToastMessage(`Post Unscheduled.`, 'success')
      } else if (item.post_at) {
        // Remove date: set post_at to null
        await updateFileMutation.mutateAsync({
          post_at: null,
        })
        showToastMessage(`Date Removed.`, 'success')
      }
      invalidateRecentsQuery()
    } catch (error) {
      showToastMessage(`Failed to unschedule post`, 'error')
    } finally {
      setIsLoading(false)
      close()
    }
  }

  const handlePostNow = useCallback(
    (close: () => void) => {
      if (!scheduledPostId) return

      if (!isCrossPostSupported && !isVideoCrossPostSupported) {
        publishScheduledPost(
          { id: scheduledPostId },
          {
            onSettled: () => {
              close()
            },
          },
        )
        return
      }

      setShouldSyncTargets(true)

      const previousStatus = item.post_status
      const previousTargets = (item.scheduled_post_targets ?? []).map((target) => ({ ...target }))

      if (workspaceId) {
        optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, item.key, (file) => {
          const currentTargets = file.scheduled_post_targets ?? []
          const shouldQueue = currentTargets.some(
            (target) => target.status !== 'published' && target.status !== 'queued',
          )

          if (!shouldQueue) {
            return file
          }

          const nextTargets = currentTargets.map((target) => {
            if (target.status === 'published') return target
            return { ...target, status: 'queued' as ScheduledPostTarget['status'] }
          })

          return {
            ...file,
            scheduled_post_targets: nextTargets,
          }
        })
      }
      setIsPublishingTargets(true)

      publishTargetsMutation.mutate(
        { scheduledPostId, targetIds: hasFailedTargets ? failedTargetIds : undefined },
        {
          onError: () => {
            setShouldSyncTargets(false)

            if (!workspaceId) return

            optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, item.key, (file) => {
              const revertedTargets = previousTargets.map((target) => ({ ...target }))

              const currentTargets = file.scheduled_post_targets ?? []
              const shouldUpdateStatus = file.post_status !== previousStatus

              const targetsDiffer =
                currentTargets.length !== revertedTargets.length ||
                currentTargets.some((current) => {
                  const original = revertedTargets.find((target) => target.id === current.id)
                  if (!original) return true
                  return original.status !== current.status
                })

              if (!shouldUpdateStatus && !targetsDiffer) {
                return file
              }

              return {
                ...file,
                post_status: previousStatus,
                scheduled_post_targets: revertedTargets,
              }
            })
          },
          onSettled: () => {
            close()
          },
        },
      )
    },
    [
      publishTargetsMutation,
      publishScheduledPost,
      scheduledPostId,
      isCrossPostSupported,
      isVideoCrossPostSupported,
      item.post_status,
      item.scheduled_post_targets,
      item.key,
      workspaceId,
      queryClient,
      failedTargetIds,
      hasFailedTargets,
    ],
  )

  const handleSyncComplete = useCallback(() => {
    setShouldSyncTargets(false)
    setIsPublishingTargets(false)
    invalidateRecentsQuery()
  }, [invalidateRecentsQuery])

  const actions = (close: () => void) =>
    isSystemRedesignEnabled ? (
      <div className={Styles.actions}>
        <Button
          size="md"
          fullWidth
          onPress={() => {
            if (!calendarPickerPostAt) return

            const status = PostStatusPropertyValue.AutoPostScheduled
            handleSchedule(new Date(calendarPickerPostAt), status)
            close()
          }}
          isDisabled={isLoading}
        >
          {item?.post_at ? 'Update Date' : 'Schedule Post'}
        </Button>
        <div className={Styles.bottomActions}>
          {item?.post_at && item.post_status !== PostStatusPropertyValue.Posted && (
            <Button
              variant="tertiary"
              frontIcon={CalendarTrash}
              size="md"
              isDisabled={isLoading}
              onPress={() => {
                handleUnschedule(close)
              }}
            >
              {item.post_status === PostStatusPropertyValue.AutoPostScheduled ? 'Unschedule' : 'Remove date'}
            </Button>
          )}
          {item.post_status !== PostStatusPropertyValue.Posted && (
            <Tooltip
              label={isBlogPost ? 'Connect your account to post on this platform' : 'Add publishing targets to post'}
              hide={!disablePostNowButton || canPostNow}
            >
              <div>
                <Button
                  variant="tertiary"
                  frontIcon={Send2}
                  fullWidth
                  size="md"
                  isDisabled={disablePostNowButton || isLoading}
                  onPress={() => handlePostNow(close)}
                >
                  {postNowButtonText}
                </Button>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    ) : (
      <div className={Styles.actions}>
        {item?.post_at && item.post_status !== PostStatusPropertyValue.Posted && (
          <Button
            variant="tertiary"
            frontIcon={CalendarTrash}
            fullWidth
            size="lg"
            isDisabled={isLoading}
            onPress={() => {
              handleUnschedule(close)
            }}
          >
            {item.post_status === PostStatusPropertyValue.AutoPostScheduled ? 'Unschedule' : 'Remove date'}
          </Button>
        )}
        {item.post_status !== PostStatusPropertyValue.Posted && (
          <Tooltip
            label={isBlogPost ? 'Connect your account to post on this platform' : 'Add publishing targets to post'}
            hide={!disablePostNowButton || canPostNow}
          >
            <div>
              <Button
                variant="tertiary"
                frontIcon={Send2}
                fullWidth
                size="lg"
                isDisabled={disablePostNowButton || isLoading}
                onPress={() => handlePostNow(close)}
              >
                {postNowButtonText}
              </Button>
            </div>
          </Tooltip>
        )}
        <Button
          size="lg"
          fullWidth
          onPress={() => {
            if (!calendarPickerPostAt) return

            const status = PostStatusPropertyValue.AutoPostScheduled
            handleSchedule(new Date(calendarPickerPostAt), status)
            close()
          }}
          isDisabled={isLoading}
        >
          {item?.post_at ? 'Update Date' : 'Schedule Post'}
        </Button>
      </div>
    )

  return (
    <div className={Styles.CalendarButtonWrapper}>
      {shouldSyncTargets && scheduledPostId && (
        <PostTargetsPublishingSync
          fileKey={item.key}
          scheduledPostId={scheduledPostId}
          onComplete={handleSyncComplete}
          onActiveChange={setIsPublishingTargets}
        />
      )}
      <DateTimePicker
        onClose={overlayState.close}
        dateTime={calendarPickerPostAt ? dayjs(calendarPickerPostAt) : undefined}
        actions={actions}
        setDateTime={(datetime) => {
          if (datetime) setCalendarPickerPostAt(datetime.toISOString())
        }}
        canEdit={canEdit}
        useSqueeze={false}
      >
        <Button
          variant="tertiary"
          size="lg"
          endIcon={canEdit ? () => <ChevronDown size={12} /> : undefined}
          isDisabled={isLoading || isPublishingTargets}
          fullWidth
          className={cx(Styles.CalendarButton, { [Styles.isDisabled]: !canEdit })}
        >
          <div>
            <Text variant="secondary" color="var(--dark-80)">
              {item?.post_at ? dayjs(item.post_at).format('ddd, MMM D') : 'Schedule Post'}
            </Text>
            {item?.post_at && (
              <Text variant="secondary" color="var(--dark-40)">
                {dayjs(item.post_at).format('h:mma')}
              </Text>
            )}
          </div>
        </Button>
      </DateTimePicker>
    </div>
  )
}
